import styled, { css } from 'styled-components';
import breakpoints from '@rsc/ui-components/breakpoints';
import styles from '@utils/styles';
import ChevronDown from '@images/chevron-down.svg';
import InvalidMessageIcon from '@images/invalid-message-icon.svg';

const FormCheck = styled.div`
  span {
    color: ${styles.colors.gray[500]};
    display: flex;
    flex-direction: column;
    font-weight: ${styles.fontWeight.bold};
    font-size: ${styles.fontSize.lg};
    margin-bottom: 0.5rem;
  }
`;

const ValidationMessage = styled.div`
  margin: 0.5rem 0 1rem 0;
  color: ${styles.colors.red[100]};
  display: block;
  background-image: url(${InvalidMessageIcon});
  background-repeat: no-repeat;
  font-size: ${styles.fontSize.sm};
  padding-left: 1.75rem;
  padding-top: 3px;
  color: ${styles.colors.red[200]};
`;

const StyledLabel = styled.label``;

const StyledForm = styled.form`
  flex: 1 0 50%;
  box-sizing: border-box;

  .hidden {
    display: none;
  }

  input,
  select,
  textarea {
    max-width: 100%;
    padding: 13px 20px;
    line-height: 16px;
    opacity: 1;
    color: ${styles.colors.gray[700]};
    margin-top: 0.5rem;
    background-color: white;
    font-size: inherit;
    letter-spacing: normal;
    border-radius: 0;
    font-weight: ${styles.fontWeight.light};
    border: solid 1px ${styles.colors.gray[200]};

    &:focus {
      outline: none;
      border: solid 0.5px ${styles.colors.blue[500]};
    }

    &::placeholder {
      color: ${styles.colors.gray[500]};
    }
  }

  select {
    font-weight: ${styles.fontWeight.light};
    background: url(${ChevronDown}) no-repeat;
    background-size: 0.65em auto, 100%;
    background-position: calc(100% - 0.75rem) center;
    appearance: none;
  }

  textarea {
    min-height: 8rem;
    resize: vertical;
  }

  span,
  label,
  .styled-label {
    color: ${styles.colors.gray[500]};
    display: flex;
    flex-direction: column;
    font-size: ${styles.fontSize.sm};
    font-weight: ${styles.fontWeight.medium};
    letter-spacing: normal;
    color: ${styles.colors.blue[100]};
    text-transform: initial;
  }

  label {
    font-size: ${styles.fontSize.base};
  }

  .asterisk {
    font-size: ${styles.fontSize.base};
    color: ${styles.colors.red[200]};
    display: contents;
  }

  .hint {
    font-size: 0.75rem;
    font-weight: 100;
    color: ${styles.colors.gray[600]};
    display: block;
  }

  ${FormCheck} {
    label {
      flex-direction: row;
      align-items: center;
      font-weight: ${styles.fontWeight.thin};
    }
    input[type='radio'],
    input[type='checkbox'],
    input[type='textbox'] {
      align-self: start;
      padding: 0;
      margin: 0;
      margin-top: 0.2rem;
      margin-right: 0.5rem;
    }
    input[type='radio'] {
      margin-top: 4px;
    }

    input[type='checkbox'] {
      flex-shrink: 0;
    }
  }
`;

const FormRow = styled.div`
  width: 100%;

  /* Only apply custom widths when we exceed the breakpoint */
  @media screen and (min-width: ${breakpoints.md}) {
    width: ${({ width }) => (width ? `${width}%` : 'auto')};
  }

  ${({ invalid }) =>
    invalid &&
    css`
      ${StyledForm} & {
        label,
        input,
        select,
        textarea {
          border-color: ${styles.colors.red[200]};
        }
        ${ValidationMessage} {
          display: block;
          color: ${styles.colors.red[200]};
          font-size: ${styles.fontSize.sm};
        }
      }
    `}
  ${({ valid }) =>
    valid &&
    css`
      ${StyledForm} & {
        label,
        input,
        select,
        textarea {
          color: green;
          border-color: green;
        }
      }
    `};
`;

const OptionalFormRow = styled.div`
  margin-bottom: 1rem;
  width: 100%;

  @media screen and (min-width: ${breakpoints.sm}) {
    width: ${({ width }) => (width ? `${width}%` : 'auto')};
  }
`;

export {
  StyledForm,
  FormRow,
  OptionalFormRow,
  FormCheck,
  ValidationMessage,
  StyledLabel,
};
